import React, { useState } from "react";

// Material-UI imports
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Alert,
  Paper,
  CircularProgress,
} from "@mui/material";
import {
  PersonAdd as PersonAddIcon,
  CloudUpload as UploadIcon,
  CheckCircleOutline as CheckIcon,
  InsertDriveFile as FileIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  EventNote as EventNoteIcon,
  Description as DescriptionIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const initialFormData = {
  name: "",
  email: "",
  contactNumber: "",
  dateOfBirth: "",
  password: "",
  appPassword: "",
  role: "employee",
  manager: "",
  offeredSalary: "",
  TDSAmount: "",
  dateOfJoining: "",
  jobLocation: "",
  workMode: "Work from Office",
  totalPaidLeave: 21,
  leaveBalance: {
    annualLeave: "",
    sickLeave: "",
    casualleave: "",
    Other: "",
  },
};

const initialFiles = {
  profilePicture: null,
  panCard: null,
  aadharCard: null,
  latestDegree: null,
  drivingLicense: null,
  previousCompanyLetter: null,
};

const steps = ['Personal Info', 'Job Details', 'Leave Balance', 'Documents'];
const requiredFields = [
  "name",
  "email",
  "contactNumber",
  "dateOfBirth",
  "password",
  "appPassword",
  "offeredSalary",
  "TDSAmount",
  "dateOfJoining",
  "jobLocation",
];

const removeSquareBrackets = (text = "") => String(text).replace(/[\[\]]/g, "");

const EmployeeForm = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [files, setFiles] = useState(initialFiles);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const getTotalLeaves = () =>
    parseInt(formData.leaveBalance.annualLeave || 0) +
    parseInt(formData.leaveBalance.sickLeave || 0) +
    parseInt(formData.leaveBalance.casualleave || 0) +
    parseInt(formData.leaveBalance.Other || 0);

  const validateCurrentStep = () => {
    if (activeStep !== 2) return true;
    const totalLeaves = getTotalLeaves();
    if (totalLeaves !== formData.totalPaidLeave) {
      setErrors((prev) => ({
        ...prev,
        leaveBalance: `Total leave allocation must be equal to ${formData.totalPaidLeave}. Current total: ${totalLeaves}`,
      }));
      setError(
        `Total leave allocation must be equal to ${formData.totalPaidLeave}.`
      );
      return false;
    }
    setErrors((prev) => ({ ...prev, leaveBalance: "" }));
    setError("");
    return true;
  };

  const validate = () => {
    const newErrors = {};

    requiredFields.forEach((field) => {
      if (
        formData[field] === undefined ||
        formData[field] === null ||
        String(formData[field]).trim() === ""
      ) {
        newErrors[field] = "This field is required.";
      }
    });

    if (!/^[a-zA-Z\s]+$/.test(formData.name)) {
      newErrors.name = "Name must only contain letters and spaces.";
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid.";
    }

    if (!/^\d{10}$/.test(formData.contactNumber)) {
      newErrors.contactNumber = "Contact number must be 10 digits.";
    }

    if (formData.offeredSalary < 0) {
      newErrors.offeredSalary = "Offered Salary must be positive.";
    }

    if (formData.TDSAmount < 0) {
      newErrors.TDSAmount = "TDS Amount must be positive.";
    }

    const totalLeaves = getTotalLeaves();

    if (totalLeaves !== formData.totalPaidLeave) {
      newErrors.leaveBalance = `Total leave days must equal ${formData.totalPaidLeave}. Current total: ${totalLeaves}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes("leaveBalance.")) {
      const leaveType = name.split(".")[1];
      setFormData(prev => ({
        ...prev,
        leaveBalance: {
          ...prev.leaveBalance,
          [leaveType]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }

    if (name.includes("leaveBalance.") && errors.leaveBalance) {
      setErrors((prev) => ({ ...prev, leaveBalance: "" }));
      setError("");
    }
  };

  const handleFileChange = (e) => {
    const { name, files: selectedFiles } = e.target;
    const file = selectedFiles[0] || null;

    if (!file) {
      setFiles((prev) => ({ ...prev, [name]: null }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setFiles((prev) => ({ ...prev, [name]: null }));
      setErrors((prev) => ({
        ...prev,
        [name]: "File size should be less than 5MB",
      }));
      return;
    }

    if (
      (name === "panCard" || name === "aadharCard") &&
      file.type !== "application/pdf"
    ) {
      setFiles((prev) => ({ ...prev, [name]: null }));
      setErrors((prev) => ({
        ...prev,
        [name]: "Only PDF files are allowed",
      }));
      return;
    }

    setFiles((prev) => ({ ...prev, [name]: file }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setFiles(initialFiles);
    setErrors({});
    setActiveStep(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!files.panCard || !files.aadharCard) {
      setError("Please upload mandatory documents: PAN Card and Aadhar Card.");
      return;
    }

    if (!validate()) {
      setError("Please fill all mandatory fields before submitting.");
      return;
    }

    setIsSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const token = sessionStorage.getItem("token");
      const formDataToSend = new FormData();

      // Append all form fields
      for (const key in formData) {
        if (key === "leaveBalance") {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      }

      // Append all files
      for (const key in files) {
        if (files[key]) {
          formDataToSend.append(key, files[key]);
        }
      }

      const response = await fetch(`${BASE_URL}/api/employees`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataToSend,
      });

      if (response.ok) {
        setSuccess("Employee created successfully!");
        resetForm();
      } else {
        const errorData = await response.json();
        setError(errorData.msg || "Error creating employee");
      }
    } catch (err) {
      console.error("Error creating employee:", err);
      setError("Error creating employee. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#f5f7fb",
      fontSize: "0.875rem",
      "& fieldset": { borderColor: "#eef0f6" },
      "&:hover fieldset": { borderColor: "#CBD5E1" },
      "&.Mui-focused fieldset": { borderColor: "#667eea" },
    },
    "& .MuiInputLabel-root": { fontSize: "0.875rem" },
  };

  const renderPersonalInfo = () => (
    <Grid container spacing={2.5}>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Full Name" name="name" value={formData.name} onChange={handleChange} error={!!errors.name} helperText={errors.name} required variant="outlined" size="small" sx={fieldSx} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} error={!!errors.email} helperText={errors.email} required variant="outlined" size="small" sx={fieldSx} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Contact Number" name="contactNumber" type="tel" value={formData.contactNumber} onChange={handleChange} error={!!errors.contactNumber} helperText={errors.contactNumber} required variant="outlined" size="small" sx={fieldSx} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Date of Birth" name="dateOfBirth" type="date" value={formData.dateOfBirth} onChange={handleChange} required variant="outlined" size="small" InputLabelProps={{ shrink: true }} sx={fieldSx} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Password" name="password" type="password" autoComplete="new-password" value={formData.password} onChange={handleChange} required variant="outlined" size="small" sx={fieldSx} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="App Password" name="appPassword" type="password" autoComplete="new-password" value={formData.appPassword} onChange={handleChange} required variant="outlined" size="small" sx={fieldSx} />
      </Grid>
    </Grid>
  );

  const selectSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#f5f7fb",
      fontSize: "0.875rem",
      "& fieldset": { borderColor: "#eef0f6" },
      "&:hover fieldset": { borderColor: "#CBD5E1" },
      "&.Mui-focused fieldset": { borderColor: "#667eea" },
    },
    "& .MuiInputLabel-root": { fontSize: "0.875rem" },
  };

  const renderJobDetails = () => (
    <Grid container spacing={2.5}>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth variant="outlined" size="small" sx={selectSx}>
          <InputLabel>Role</InputLabel>
          <Select name="role" value={formData.role} onChange={handleChange} label="Role" required>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="manager">Manager</MenuItem>
            <MenuItem value="employee">Employee</MenuItem>
            <MenuItem value="store manager">Store Manager</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Manager ID"
          name="manager"
          value={formData.manager}
          onChange={handleChange}
          variant="outlined"
          size="small"
          placeholder="e.g., INFY-EMP-001"
          helperText="Enter Manager ID (e.g., INFY-EMP-001)"
          sx={fieldSx}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Offered Salary" name="offeredSalary" type="number" value={formData.offeredSalary} onChange={handleChange} error={!!errors.offeredSalary} helperText={errors.offeredSalary} required variant="outlined" size="small" sx={fieldSx} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="TDS Amount" name="TDSAmount" type="number" value={formData.TDSAmount} onChange={handleChange} error={!!errors.TDSAmount} helperText={errors.TDSAmount} required variant="outlined" size="small" sx={fieldSx} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Date of Joining" name="dateOfJoining" type="date" value={formData.dateOfJoining} onChange={handleChange} required variant="outlined" size="small" InputLabelProps={{ shrink: true }} sx={fieldSx} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Job Location" name="jobLocation" value={formData.jobLocation} onChange={handleChange} required variant="outlined" size="small" sx={fieldSx} />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth variant="outlined" size="small" sx={selectSx}>
          <InputLabel>Work Mode</InputLabel>
          <Select name="workMode" value={formData.workMode} onChange={handleChange} label="Work Mode" required>
            <MenuItem value="Work from Office">Work from Office</MenuItem>
            <MenuItem value="Work from Home">Work from Home</MenuItem>
            <MenuItem value="Hybrid">Hybrid</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  const renderLeaveBalance = () => (
    <Grid container spacing={2.5}>
      <Grid item xs={12}>
        {errors.leaveBalance && (
          <Alert severity="error" sx={{ mb: 1, borderRadius: "12px", fontSize: "0.875rem" }}>
            {errors.leaveBalance}
          </Alert>
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Total Paid Leave" name="totalPaidLeave" type="number" value={formData.totalPaidLeave} onChange={handleChange} variant="outlined" size="small" InputProps={{ readOnly: true }} sx={fieldSx} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Annual Leave" name="leaveBalance.annualLeave" type="number" value={formData.leaveBalance.annualLeave} onChange={handleChange} required variant="outlined" size="small" sx={fieldSx} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Sick Leave" name="leaveBalance.sickLeave" type="number" value={formData.leaveBalance.sickLeave} onChange={handleChange} required variant="outlined" size="small" sx={fieldSx} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Casual Leave" name="leaveBalance.casualleave" type="number" value={formData.leaveBalance.casualleave} onChange={handleChange} required variant="outlined" size="small" sx={fieldSx} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Other Leave" name="leaveBalance.Other" type="number" value={formData.leaveBalance.Other} onChange={handleChange} required variant="outlined" size="small" sx={fieldSx} />
      </Grid>
    </Grid>
  );

  const FileUploadBox = ({ name, label, required, accept, file }) => (
    <Box>
    <Box
      sx={{
        border: file ? "2px solid #86EFAC" : errors[name] ? "2px solid #EF4444" : "2px dashed transparent",
        borderImage: file ? "none" : "linear-gradient(135deg, #667eea, #764ba2) 1",
        borderRadius: "14px",
        p: 3,
        textAlign: "center",
        cursor: "pointer",
        transition: "all 0.3s ease",
        bgcolor: file ? "#F0FDF4" : "#FAFBFC",
        position: "relative",
        overflow: "hidden",
        "&:hover": {
          bgcolor: file ? "#F0FDF4" : "#f0f0ff",
          transform: "translateY(-2px)",
          boxShadow: "0 4px 16px rgba(102, 126, 234, 0.15)",
        },
      }}
      component="label"
    >
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: "14px",
          background: file
            ? "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
            : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          mb: 1.5,
          boxShadow: file
            ? "0 4px 12px rgba(67, 233, 123, 0.3)"
            : "0 4px 12px rgba(102, 126, 234, 0.3)",
        }}
      >
        {file ? (
          <CheckIcon sx={{ fontSize: 26, color: "#fff" }} />
        ) : (
          <UploadIcon sx={{ fontSize: 26, color: "#fff" }} />
        )}
      </Box>
      <Typography
        sx={{
          fontSize: "0.875rem",
          fontWeight: 600,
          color: "#1a1a2e",
          mb: 0.5,
        }}
      >
        {label}
        {required && (
          <Typography
            component="span"
            sx={{ color: "#EF4444", ml: 0.5, fontSize: "0.875rem" }}
          >
            *
          </Typography>
        )}
      </Typography>
      <Typography sx={{ fontSize: "0.75rem", color: "#8b8fa3", mb: 1.5 }}>
        {file ? "" : "Click to upload or drag and drop"}
      </Typography>
      <input
        type="file"
        name={name}
        onChange={handleFileChange}
        accept={accept}
        style={{ display: "none" }}
      />
      {file && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.75,
            bgcolor: "#ECFDF5",
            borderRadius: "8px",
            px: 1.5,
            py: 0.5,
            width: "100%",
          }}
        >
          <FileIcon sx={{ fontSize: 16, color: "#059669" }} />
          <Typography sx={{ fontSize: "0.8125rem", color: "#059669", fontWeight: 500, wordBreak: "break-word" }}>
            {removeSquareBrackets(file.name)}
          </Typography>
        </Box>
      )}
    </Box>
    {errors[name] && (
      <Typography sx={{ color: "#EF4444", fontSize: "0.75rem", mt: 0.6, ml: 0.3 }}>
        {errors[name]}
      </Typography>
    )}
    </Box>
  );

  const renderDocuments = () => (
    <Grid container spacing={2.5}>
      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: "10px",
            border: "1px solid #FDE68A",
            bgcolor: "#FFFBEB",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.82rem",
              color: "#92400E",
              fontWeight: 500,
              lineHeight: 1.45,
            }}
          >
            Note: Fields marked with (*) are mandatory. PAN Card and Aadhar Card accept only PDF files (max size 5MB).
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <FileUploadBox
          name="profilePicture"
          label="Profile Picture"
          required={false}
          accept=".jpg,.jpeg,.png"
          file={files.profilePicture}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FileUploadBox
          name="panCard"
          label="PAN Card"
          required={true}
          accept=".pdf,application/pdf"
          file={files.panCard}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FileUploadBox
          name="aadharCard"
          label="Aadhar Card"
          required={true}
          accept=".pdf,application/pdf"
          file={files.aadharCard}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FileUploadBox
          name="latestDegree"
          label="Latest Degree"
          required={false}
          accept=".pdf,.jpg,.jpeg,.png"
          file={files.latestDegree}
        />
      </Grid>
    </Grid>
  );

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderPersonalInfo();
      case 1:
        return renderJobDetails();
      case 2:
        return renderLeaveBalance();
      case 3:
        return renderDocuments();
      default:
        return 'Unknown step';
    }
  };

  // Step section titles and icons
  const stepTitles = [
    "Personal Information",
    "Job & Compensation Details",
    "Leave Balance Allocation",
    "Document Uploads",
  ];

  const stepIcons = [
    <PersonIcon sx={{ fontSize: 20, color: "#fff" }} />,
    <WorkIcon sx={{ fontSize: 20, color: "#fff" }} />,
    <EventNoteIcon sx={{ fontSize: 20, color: "#fff" }} />,
    <DescriptionIcon sx={{ fontSize: 20, color: "#fff" }} />,
  ];

  return (
    <PageContainer>
      {/* Gradient Page Header */}
      <PageHeader
        title="Create New Employee"
        subtitle="Add a new team member to your organization"
        icon={<PersonAddIcon />}
        gradient
      />

      {/* Custom Stepper Card */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: "16px",
          border: "1px solid #eef0f6",
          bgcolor: "#fff",
          boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            position: "relative",
          }}
        >
          {steps.map((label, index) => {
            const isActive = index === activeStep;
            const isCompleted = index < activeStep;
            return (
              <Box
                key={label}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  flex: 1,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {/* Connecting Line */}
                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 20,
                      left: "50%",
                      width: "100%",
                      height: 3,
                      bgcolor: isCompleted ? "transparent" : "#eef0f6",
                      background: isCompleted
                        ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                        : "#eef0f6",
                      borderRadius: "2px",
                      zIndex: 0,
                    }}
                  />
                )}

                {/* Step Circle */}
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "50%",
                    background: isActive || isCompleted
                      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                      : "#eef0f6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1,
                    zIndex: 1,
                    boxShadow: isActive
                      ? "0 4px 16px rgba(102, 126, 234, 0.4)"
                      : "none",
                    transition: "all 0.3s ease",
                    border: isActive ? "3px solid rgba(102, 126, 234, 0.2)" : "none",
                  }}
                >
                  {isCompleted ? (
                    <CheckIcon sx={{ fontSize: 20, color: "#fff" }} />
                  ) : isActive ? (
                    stepIcons[index]
                  ) : (
                    <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#8b8fa3" }}>
                      {index + 1}
                    </Typography>
                  )}
                </Box>

                {/* Step Label */}
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? "#667eea" : isCompleted ? "#1a1a2e" : "#8b8fa3",
                    textAlign: "center",
                    lineHeight: 1.3,
                    px: 0.5,
                    transition: "all 0.3s ease",
                  }}
                >
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Paper>

      {/* Success/Error Messages */}
      {success && (
        <Alert
          severity="success"
          sx={{
            mb: 3,
            borderRadius: "12px",
            fontSize: "0.875rem",
            border: "1px solid #BBF7D0",
          }}
        >
          {success}
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: "12px",
            fontSize: "0.875rem",
            border: "1px solid #FECACA",
          }}
        >
          {error}
        </Alert>
      )}

      {/* Form Card */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          border: "1px solid #eef0f6",
          bgcolor: "#fff",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
        }}
      >
        {/* Gradient Section Title */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            px: 3.5,
            py: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              bgcolor: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {stepIcons[activeStep]}
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "1rem", color: "#fff" }}>
              {stepTitles[activeStep]}
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.6)" }}>
              Step {activeStep + 1} of {steps.length}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 3, sm: 4 } }}>
          <form onSubmit={handleSubmit} encType="multipart/form-data">
            {getStepContent(activeStep)}

            {/* Navigation Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1.5,
                mt: 4,
                pt: 3,
                borderTop: "1px solid #eef0f6",
              }}
            >
              <Button
                disabled={activeStep === 0}
                onClick={() => setActiveStep((prev) => prev - 1)}
                variant="outlined"
                startIcon={<ArrowBackIcon sx={{ fontSize: "18px !important" }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "12px",
                  borderColor: "#CBD5E1",
                  color: "#5a5f7a",
                  px: 3,
                  py: 1,
                  "&:hover": {
                    borderColor: "#667eea",
                    color: "#667eea",
                    bgcolor: "#f0f0ff",
                  },
                  "&:disabled": {
                    borderColor: "#eef0f6",
                    color: "#CBD5E1",
                  },
                }}
              >
                Back
              </Button>

              {activeStep === steps.length - 1 ? (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <PersonAddIcon />
                    )
                  }
                  sx={{
                    px: 4,
                    py: 1.2,
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "12px",
                    fontSize: "0.95rem",
                    boxShadow: "0 4px 16px rgba(102, 126, 234, 0.3)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)",
                      boxShadow: "0 6px 24px rgba(102, 126, 234, 0.4)",
                    },
                    "&:disabled": {
                      bgcolor: "#eef0f6",
                      color: "#8b8fa3",
                    },
                  }}
                >
                  {isSubmitting ? "Creating..." : "Create Employee"}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => {
                    if (!validateCurrentStep()) return;
                    setActiveStep((prev) => prev + 1);
                  }}
                  endIcon={<ArrowForwardIcon sx={{ fontSize: "18px !important" }} />}
                  sx={{
                    px: 4,
                    py: 1.2,
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "12px",
                    fontSize: "0.95rem",
                    boxShadow: "0 4px 16px rgba(102, 126, 234, 0.3)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)",
                      boxShadow: "0 6px 24px rgba(102, 126, 234, 0.4)",
                    },
                  }}
                >
                  Next Step
                </Button>
              )}
            </Box>
          </form>
        </Box>
      </Paper>
    </PageContainer>
  );
};

export default EmployeeForm;